import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
   hasContentEdits,
   useCampaignV2Store,
} from '@forwardflow/features/campaign-v2/store/editor.store'
import {
   workingCopyKey,
   keepWorkingCopy,
   restoreWorkingCopy,
   forgetWorkingCopy,
} from '@forwardflow/features/campaign-v2/store/working-copy'
import { useCampaignV2Editor } from '@forwardflow/features/campaign-v2/hooks/useCampaignV2Editor'
import {
   useBusinessCategories,
   usePreview,
   useV2Readiness,
} from '@forwardflow/features/campaign-v2/queries/campaign-v2.queries'
import { campaignV2Api } from '@forwardflow/features/campaign-v2/services'
import {
   CampaignV2Error,
   IConfigurationRead,
} from '@forwardflow/features/campaign-v2/interfaces/campaign-v2.types'
import { newConfiguration } from '@forwardflow/features/campaign-v2/utils/config'
import { AudienceStep } from '@forwardflow/features/campaign-v2/components/steps/AudienceStep'
import { ContentStep } from '@forwardflow/features/campaign-v2/components/steps/ContentStep'
import { VoiceStep } from '@forwardflow/features/campaign-v2/components/steps/VoiceStep'
import { TeamStep } from '@forwardflow/features/campaign-v2/components/steps/TeamStep'
import { NumbersStep } from '@forwardflow/features/campaign-v2/components/steps/NumbersStep'
import { Loading } from '@forwardflow/features/campaign-v2/components/shared'
import { useAppStore } from '@forwardflow/store/appStore'
import { useFlowBase } from '../../hooks/useFlowBase'
import { FlowShell } from '../frame/FlowShell'
import { FlowIcon } from '../ui/FlowIcon'
import { Cadence } from './Cadence'
import { LeadType } from './LeadType'
import { Sidebar, WorkspaceLocation } from './Sidebar'
import {
   changesBetween,
   CONTENT_SECTIONS,
   ContentSection,
   poolName,
   WorkspaceSection,
} from './model'
import '../campaign-flow.css'
import './workspace.css'

const SECTIONS: WorkspaceSection[] = [
   'overview',
   'audience',
   'cadence',
   'content',
   'voice',
   'team',
   'numbers',
   'review',
   'add-type',
]
/** The complete server configuration is the working model. Navigation never saves it. */
export const CampaignWorkspace = () => {
   const { id } = useParams()
   const [query, setQuery] = useSearchParams()
   const { base, campaigns, preview: demo } = useFlowBase()
   const navigate = useNavigate()
   const org = useAppStore((s) => s.currentOrganization)
   const switchOrg = useAppStore((s) => s.setCurrentOrganization)
   const requestedOrg = query.get('organizationId')
   const store = useCampaignV2Store()
   const {
      save,
      activate,
      publish,
      pause,
      acceptConflict,
      saving,
      activating,
      error,
   } = useCampaignV2Editor()
   const { data: categories, error: categoriesError } = useBusinessCategories()
   const { data: readiness, error: readinessError } = useV2Readiness()
   const {
      data: serverPreview,
      error: previewError,
      mutate: refreshPreview,
   } = usePreview(
      query.get('section') === 'review' ? store.campaignId : null,
      store.revision
   )
   const categoriesReady = Boolean(categories?.length)
   const canInitialize = Boolean(id) || categoriesReady
   const setCategoryLabels = store.setCategoryLabels
   const [initial, setInitialState] = useState<IConfigurationRead | null>(null)
   const initialRef = useRef<IConfigurationRead | null>(null)
   const setInitial = (read: IConfigurationRead | null) => {
      initialRef.current = read
      setInitialState(read)
   }
   const draftCacheKey = workingCopyKey(demo ? 'preview' : org, id)
   const pendingContent = Object.entries(store.contentDrafts).find(
      ([, d]) => d.mode !== 'pick'
   )
   const unsaved = store.dirty || Boolean(pendingContent)
   const [loadError, setLoadError] = useState('')
   const [loading, setLoading] = useState(true)
   const [reload, setReload] = useState(0)
   const [mobileOpen, setMobileOpen] = useState(false)
   const [applyError, setApplyError] = useState('')
   const section = (
      query.get('section') === 'content' && query.get('content') === 'personas'
         ? 'voice'
         : SECTIONS.includes(query.get('section') as WorkspaceSection)
           ? query.get('section')
           : id
             ? 'cadence'
             : 'audience'
   ) as WorkspaceSection
   const location: WorkspaceLocation = {
      section,
      poolId: query.get('pool') || undefined,
      content: CONTENT_SECTIONS.some((s) => s.id === query.get('content'))
         ? (query.get('content') as ContentSection)
         : 'description',
   }
   const go = useCallback(
      (next: WorkspaceLocation) => {
         const q = new URLSearchParams(query)
         q.set('section', next.section)
         if (next.poolId) q.set('pool', next.poolId)
         else q.delete('pool')
         if (next.content) q.set('content', next.content)
         else q.delete('content')
         setQuery(q)
         setMobileOpen(false)
      },
      [query, setQuery]
   )

   useEffect(() => {
      if (
         requestedOrg &&
         requestedOrg !== org &&
         /^[a-f0-9]{32}$/.test(requestedOrg)
      )
         switchOrg(requestedOrg)
   }, [requestedOrg, org, switchOrg])
   useEffect(() => {
      if (requestedOrg && requestedOrg !== org && !demo) return
      if (!demo && !org) return
      if (!canInitialize) return
      let cancelled = false
      let loaded = false
      useCampaignV2Store.getState().reset()
      setInitial(null)
      setLoadError('')
      setLoading(true)
      if (id) {
         campaignV2Api()
            .readConfiguration(id)
            .then((read) => {
               if (cancelled) return
               useCampaignV2Store.getState().applyRead(read)
               const restored = restoreWorkingCopy(draftCacheKey, read)
               setInitial(restored?.initial || structuredClone(read))
               loaded = true
            })
            .catch((e) => {
               if (cancelled) return
               if (
                  !demo &&
                  e instanceof CampaignV2Error &&
                  e.status === 409 &&
                  e.message === 'Legacy campaigns use the existing editor'
               ) {
                  navigate(`/campaign/${encodeURIComponent(id)}/phone-number`, {
                     replace: true,
                  })
                  return
               }
               setLoadError(
                  (e as Error).message || 'Could not load this campaign.'
               )
            })
            .finally(() => !cancelled && setLoading(false))
      } else {
         /* A new campaign opens with the general reactivate cadence preset —
            audience and content still come from the user (or the save-time
            defaults). */
         useCampaignV2Store.getState().startNew(newConfiguration([]), '')
         restoreWorkingCopy(draftCacheKey)
         loaded = true
         setLoading(false)
      }
      return () => {
         cancelled = true
         if (
            loaded &&
            useCampaignV2Store.getState().campaignId === (id || null)
         )
            keepWorkingCopy(draftCacheKey, initialRef.current)
      }
   }, [
      id,
      org,
      requestedOrg,
      demo,
      canInitialize,
      reload,
      draftCacheKey,
      navigate,
   ])
   useEffect(() => {
      if (categories?.length)
         setCategoryLabels(
            Object.fromEntries(
               categories.map((c) => [
                  c.id,
                  c.name.charAt(0).toUpperCase() + c.name.slice(1),
               ])
            )
         )
   }, [categories, setCategoryLabels, loading])
   useEffect(() => {
      const warn = (e: BeforeUnloadEvent) => {
         const current = useCampaignV2Store.getState()
         if (current.dirty || hasContentEdits(current.contentDrafts)) {
            e.preventDefault()
            e.returnValue = ''
         }
      }
      window.addEventListener('beforeunload', warn)
      return () => window.removeEventListener('beforeunload', warn)
   }, [])

   const finishContent = () => {
      if (!pendingContent) return
      const [poolId] = JSON.parse(pendingContent[0]) as [string]
      const slot = pendingContent[1].slot
      const content = CONTENT_SECTIONS.find((s) => s.id === slot)?.id
      go({
         section: slot === 'personas' ? 'voice' : 'content',
         poolId,
         content: content || 'description',
      })
   }
   const onSave = async () => {
      if (pendingContent) {
         finishContent()
         return null
      }
      const campaignId = await save()
      if (campaignId) {
         forgetWorkingCopy(draftCacheKey)
         const current = useCampaignV2Store.getState()
         setInitial({
            campaignId,
            name: current.name,
            configuration: current.config!,
            revision: current.revision,
            status: current.status,
            publishedVersionId: current.publishedVersionId,
            activeVersionId: current.activeVersionId,
            hasUnpublishedChanges: current.hasUnpublishedChanges,
         })
      }
      if (campaignId && !id) {
         const q = new URLSearchParams(query)
         q.set('section', section)
         navigate(`${base}/${encodeURIComponent(campaignId)}/edit?${q}`, {
            replace: true,
         })
      }
      if (campaignId) void refreshPreview()
      return campaignId
   }
   const footer = {
      action: 'Save draft',
      onAction: () => {
         void onSave()
      },
      loading: saving,
   }
   const config = store.config
   const failure =
      loadError ||
      (categoriesError as Error)?.message ||
      (readinessError as Error)?.message
   const selectedPool = config?.contentPools.find(
      (p) => p.id === location.poolId
   )
   const personaPool = selectedPool || config?.contentPools[0]
   const changes =
      config && initial ? changesBetween(initial.configuration, config) : []
   if (initial && initial.name !== store.name)
      changes.unshift('Campaign name changed')
   const contentLabel = CONTENT_SECTIONS.find(
      (s) => s.id === location.content
   )?.label
   const sectionTitle =
      section === 'content' && selectedPool
         ? `${poolName(config!, selectedPool, store.categoryLabels)} / ${contentLabel || 'Content'}`
         : {
              overview: 'Overview',
              audience: 'Audience',
              cadence: 'Cadence',
              content: 'Content',
              voice: 'Voice & persona',
              team: 'Agent routing',
              numbers: 'Phone numbers',
              review: 'Review changes',
              'add-type': 'Add subtype',
           }[section]

   return (
      <FlowShell className="lg:!overflow-hidden !pb-4">
         <div
            className="cf campaign-workspace"
            data-testid="campaign-workspace"
         >
            <header className="cw-head">
               <div>
                  <Link to={campaigns} className="cw-link mb-2">
                     <FlowIcon name="back" size={13} />
                     All campaigns
                  </Link>
                  <div className="cw-headline">
                     <h1>
                        {loading
                           ? 'Opening campaign…'
                           : store.name || 'New campaign'}
                     </h1>
                     {config && (
                        <span
                           className={`cw-status ${store.status}`}
                           data-testid="campaign-status"
                        >
                           {store.status}
                        </span>
                     )}
                  </div>
                  <small>{sectionTitle}</small>
               </div>
               <div className="cw-actions">
                  {unsaved && (
                     <button
                        className="cw-button"
                        type="button"
                        onClick={() => {
                           if (
                              window.confirm(
                                 'Discard your unsaved campaign and content changes?'
                              )
                           ) {
                              forgetWorkingCopy(draftCacheKey)
                              useCampaignV2Store.getState().reset()
                              setReload((v) => v + 1)
                           }
                        }}
                     >
                        Discard changes
                     </button>
                  )}
                  <button
                     className="cw-button"
                     type="button"
                     onClick={() => void onSave()}
                     disabled={
                        !config ||
                        saving ||
                        activating ||
                        /* A named draft always saves: audience, cadence and
                           content fall back to the save-time defaults. */
                        (!pendingContent &&
                           (!!store.conflict ||
                              !store.dirty ||
                              !store.name.trim()))
                     }
                     data-testid="workspace-save"
                  >
                     {saving
                        ? 'Saving…'
                        : pendingContent
                          ? 'Finish content edits'
                          : 'Save draft'}
                  </button>
                  {store.status === 'active' && (
                     <button
                        type="button"
                        className="cw-button"
                        data-testid="workspace-pause"
                        disabled={saving || activating || unsaved}
                        onClick={() => void pause()}
                     >
                        Pause campaign
                     </button>
                  )}
                  <button
                     className="cw-button primary"
                     type="button"
                     onClick={() => go({ section: 'review' })}
                     disabled={!config}
                     data-testid="workspace-review"
                  >
                     Review changes
                  </button>
               </div>
            </header>
            {unsaved && (
               <div className="cw-banner" role="status">
                  {pendingContent ? (
                     <>
                        Unsaved content — finish editing and save it before
                        saving the campaign.{' '}
                        <button
                           type="button"
                           className="cw-link ml-2"
                           onClick={finishContent}
                        >
                           Return to content
                        </button>
                     </>
                  ) : (
                     'Unsaved campaign changes'
                  )}
               </div>
            )}
            {store.conflict && (
               <div className="cw-banner cw-error" role="alert">
                  The campaign changed elsewhere. Your edits have been kept for
                  comparison.{' '}
                  <button
                     type="button"
                     className="cw-link ml-3"
                     onClick={() => {
                        if (
                           window.confirm(
                              'Replace your unsaved changes with the latest saved version?'
                           )
                        ) {
                           forgetWorkingCopy(draftCacheKey)
                           useCampaignV2Store.setState({ contentDrafts: {} })
                           setInitial(store.conflict)
                           acceptConflict()
                        }
                     }}
                  >
                     Load the latest saved version
                  </button>
               </div>
            )}
            {error && (
               <div className="cw-banner cw-error" role="alert">
                  {error}
               </div>
            )}
            {failure ? (
               <div className="cw-surface">
                  <h3>Could not open the campaign</h3>
                  <p className="cw-inline-note">{failure}</p>
                  <button
                     type="button"
                     className="cw-button"
                     onClick={() => setReload((v) => v + 1)}
                  >
                     Try again
                  </button>
               </div>
            ) : readiness && !readiness.enabled ? (
               <div className="cw-surface">
                  <h3>Campaign V2 is not enabled for this organization</h3>
                  <p className="cw-inline-note">
                     {readiness.reasons.join(' ')}
                  </p>
               </div>
            ) : loading || !config ? (
               <Loading label="Loading the saved configuration" />
            ) : (
               <>
                  <button
                     type="button"
                     className="cw-button cw-mobile-nav"
                     aria-expanded={mobileOpen}
                     onClick={() => setMobileOpen(!mobileOpen)}
                  >
                     <FlowIcon name="chevDown" size={16} />
                     Campaign menu · {sectionTitle}
                  </button>
                  <div className="cw-layout">
                     <Sidebar
                        config={config}
                        labels={store.categoryLabels}
                        location={location}
                        onGo={go}
                        open={mobileOpen}
                     />
                     <main className="cw-panel" data-testid="workspace-panel">
                        <fieldset
                           disabled={saving || activating}
                           className="min-w-0"
                        >
                           {section === 'cadence' && (
                              <Cadence
                                 onContent={() => go({ section: 'content' })}
                              />
                           )}
                           {section === 'audience' && (
                              <AudienceStep footer={footer} />
                           )}
                           {section === 'content' &&
                              selectedPool &&
                              (location.content === 'description' ? (
                                 <LeadType
                                    key={selectedPool.id}
                                    poolId={selectedPool.id}
                                    onAdded={() => {}}
                                    onContent={(content) =>
                                       go({
                                          section: 'content',
                                          poolId: selectedPool.id,
                                          content,
                                       })
                                    }
                                 />
                              ) : (
                                 <ContentStep
                                    key={`${selectedPool.id}:${location.content}`}
                                    poolId={selectedPool.id}
                                    slot={location.content}
                                    draftOnly
                                    footer={undefined}
                                 />
                              ))}
                           {section === 'content' && !selectedPool && (
                              <div>
                                 <div className="cw-title">
                                    <div>
                                       <h2>Content</h2>
                                       <p>
                                          Start with general content. Add a
                                          subtype only when some leads need
                                          different scripts or messages.
                                       </p>
                                    </div>
                                    <button
                                       type="button"
                                       className="cw-button"
                                       onClick={() =>
                                          go({
                                             section: config.businessTypeIds
                                                .length
                                                ? 'add-type'
                                                : 'audience',
                                          })
                                       }
                                    >
                                       Add subtype
                                    </button>
                                 </div>
                                 {!config.businessTypeIds.length && (
                                    <div className="cw-surface">
                                       <h3>Choose a business type first</h3>
                                       <p className="cw-inline-note">
                                          Choose Buyer, Seller, Lending or
                                          another business type in Audience. You
                                          can then use a template or create your
                                          own content.
                                       </p>
                                       <button
                                          type="button"
                                          className="cw-button primary"
                                          onClick={() =>
                                             go({ section: 'audience' })
                                          }
                                       >
                                          Choose business type
                                       </button>
                                    </div>
                                 )}
                                 {config.businessTypeIds.map((category) => (
                                    <div className="cw-surface" key={category}>
                                       <h3>
                                          {store.categoryLabels[category] ||
                                             `Category ${category}`}
                                       </h3>
                                       {config.contentPools
                                          .filter(
                                             (p) =>
                                                p.businessTypeId === category
                                          )
                                          .map((p) => (
                                             <button
                                                type="button"
                                                key={p.id}
                                                className="cw-nav-row"
                                                onClick={() =>
                                                   go({
                                                      section: 'content',
                                                      poolId: p.id,
                                                      content: 'description',
                                                   })
                                                }
                                             >
                                                <span className="flex-1">
                                                   {poolName(
                                                      config,
                                                      p,
                                                      store.categoryLabels
                                                   )}
                                                </span>
                                                <small>
                                                   {p.outboundScripts.length}{' '}
                                                   scripts ·{' '}
                                                   {p.strategies.length}{' '}
                                                   outbound content groups
                                                </small>
                                                <FlowIcon
                                                   name="chev"
                                                   size={14}
                                                />
                                             </button>
                                          ))}
                                    </div>
                                 ))}
                              </div>
                           )}
                           {section === 'add-type' && (
                              <LeadType
                                 key="new"
                                 onChooseCategory={() =>
                                    go({ section: 'audience' })
                                 }
                                 onAdded={(poolId) =>
                                    go({
                                       section: 'content',
                                       poolId,
                                       content: 'description',
                                    })
                                 }
                              />
                           )}
                           {section === 'voice' && !store.campaignId && (
                              <div className="cw-surface">
                                 <h3>Save your draft first</h3>
                                 <p className="cw-inline-note">
                                    Save the draft to manage its voice and phone
                                    numbers. This does not activate outreach.
                                 </p>
                                 {(!store.name.trim() ||
                                    !config.businessTypeIds.length) && (
                                    <button
                                       type="button"
                                       className="cw-button"
                                       onClick={() =>
                                          go({ section: 'audience' })
                                       }
                                    >
                                       Add name and business type
                                    </button>
                                 )}
                                 {!config.cadence.length && (
                                    <button
                                       type="button"
                                       className="cw-button"
                                       onClick={() =>
                                          go({ section: 'cadence' })
                                       }
                                    >
                                       Add cadence
                                    </button>
                                 )}
                                 <button
                                    type="button"
                                    className="cw-button primary"
                                    onClick={() => void onSave()}
                                    disabled={
                                       saving ||
                                       !store.name.trim() ||
                                       !config.businessTypeIds.length ||
                                       !config.cadence.length
                                    }
                                 >
                                    Save draft
                                 </button>
                              </div>
                           )}
                           {section === 'voice' && store.campaignId && (
                              <>
                                 <VoiceStep footer={footer} />
                              </>
                           )}
                           {section === 'voice' && personaPool && (
                              <div
                                 className="mt-8 border-t border-white/10 pt-6"
                                 data-testid="voice-persona-section"
                              >
                                 {config.contentPools.length > 1 && (
                                    <label className="cw-field mb-4">
                                       <span>Persona for</span>
                                       <select
                                          aria-label="Persona for lead type"
                                          value={personaPool.id}
                                          onChange={(e) =>
                                             go({
                                                section: 'voice',
                                                poolId: e.target.value,
                                                content: 'personas',
                                             })
                                          }
                                       >
                                          {config.contentPools.map((p) => (
                                             <option key={p.id} value={p.id}>
                                                {poolName(
                                                   config,
                                                   p,
                                                   store.categoryLabels
                                                )}
                                             </option>
                                          ))}
                                       </select>
                                    </label>
                                 )}
                                 <ContentStep
                                    key={`voice-persona:${personaPool.id}`}
                                    poolId={personaPool.id}
                                    slot="personas"
                                    draftOnly
                                 />
                              </div>
                           )}
                           {section === 'numbers' && (
                              <NumbersStep
                                 footer={footer}
                                 onSave={() => void onSave()}
                                 canSave={Boolean(
                                    store.name.trim() &&
                                       !saving &&
                                       !store.conflict
                                 )}
                              />
                           )}
                           {section === 'team' && <TeamStep footer={footer} />}
                           {section === 'overview' && (
                              <div>
                                 <div className="cw-title">
                                    <div>
                                       <h2>Campaign configuration</h2>
                                       <p>
                                          Open a section in the sidebar to
                                          change it. Your saved selections are
                                          already loaded.
                                       </p>
                                    </div>
                                 </div>
                                 <div className="cw-surface">
                                    {[
                                       ['Name', store.name],
                                       ['Status', store.status],
                                       [
                                          'Audience',
                                          config.source.mode === 'manual'
                                             ? 'Manually assigned leads'
                                             : `${config.source.criteria.length} filters`,
                                       ],
                                       [
                                          'Lead types',
                                          String(config.contentPools.length),
                                       ],
                                       [
                                          'Cadence',
                                          `${config.cadence.length} actions`,
                                       ],
                                       [
                                          'Phone numbers',
                                          String(
                                             config.phoneNumbers?.length || 0
                                          ),
                                       ],
                                    ].map(([label, value]) => (
                                       <div className="cw-change" key={label}>
                                          <span className="cw-muted">
                                             {label}
                                          </span>
                                          <span>{value}</span>
                                       </div>
                                    ))}
                                 </div>
                              </div>
                           )}
                           {section === 'review' && (
                              <div data-testid="workspace-review-panel">
                                 <div className="cw-title">
                                    <div>
                                       <h2>Review changes</h2>
                                       <p>
                                          Saving a draft and applying it are
                                          separate actions.
                                       </p>
                                    </div>
                                 </div>
                                 <div className="cw-surface">
                                    <h3>Changes since opening</h3>
                                    {changes.length ? (
                                       changes.map((c) => (
                                          <div className="cw-change" key={c}>
                                             {c}
                                          </div>
                                       ))
                                    ) : (
                                       <p className="cw-inline-note">
                                          {store.hasUnpublishedChanges
                                             ? 'There are saved draft changes to review.'
                                             : 'No campaign configuration changes in this session.'}
                                       </p>
                                    )}
                                 </div>
                                 {config.cadence.length > 0 && (
                                    <div className="cw-surface">
                                       <h3>Cadence</h3>
                                       <p className="cw-inline-note">
                                          {config.cadence.length} actions over{' '}
                                          {Math.max(
                                             ...config.cadence.map((s) => s.day)
                                          )}{' '}
                                          days. Existing enrolled leads retain
                                          their scheduled tasks; saving a new
                                          cadence does not replay completed
                                          work.
                                       </p>
                                    </div>
                                 )}
                                 {store.dirty ? (
                                    <div className="cw-surface">
                                       <p className="cw-inline-note">
                                          Save the draft to check the exact
                                          configuration with the server before
                                          applying it.
                                       </p>
                                       <button
                                          type="button"
                                          className="cw-button primary"
                                          disabled={saving || !!store.conflict}
                                          onClick={() => void onSave()}
                                       >
                                          Save draft and check
                                       </button>
                                    </div>
                                 ) : (
                                    <div className="cw-surface">
                                       <h3>
                                          {serverPreview?.ready
                                             ? 'Configuration check passed'
                                             : 'Configuration check'}
                                       </h3>
                                       {previewError && (
                                          <p className="cw-inline-note text-red-300">
                                             Could not check readiness. Try
                                             again.
                                          </p>
                                       )}
                                       {serverPreview?.reasons.map((reason) => (
                                          <p
                                             key={reason}
                                             className="cw-inline-note"
                                          >
                                             {reason.replace(/_/g, ' ')}
                                          </p>
                                       ))}
                                       {!serverPreview && !previewError && (
                                          <Loading label="Checking saved draft" />
                                       )}
                                       <p className="cw-inline-note">
                                          {!serverPreview
                                             ? 'Number purchases will be checked with the draft.'
                                             : serverPreview.numbersToPurchase
                                               ? `${serverPreview.numbersToPurchase} reserved numbers will be purchased when activated.`
                                               : 'No new number purchases required.'}
                                       </p>
                                       <div className="cw-actions">
                                          <button
                                             type="button"
                                             className="cw-button"
                                             onClick={() =>
                                                void refreshPreview()
                                             }
                                          >
                                             Check again
                                          </button>
                                          <button
                                             type="button"
                                             className="cw-button primary"
                                             disabled={
                                                !serverPreview?.ready ||
                                                saving ||
                                                activating ||
                                                !!store.conflict ||
                                                !store.hasUnpublishedChanges
                                             }
                                             onClick={async () => {
                                                setApplyError('')
                                                const ok =
                                                   store.status === 'active'
                                                      ? await activate()
                                                      : await publish()
                                                if (!ok)
                                                   setApplyError(
                                                      'The update was not applied. Review the error above and try again.'
                                                   )
                                             }}
                                             data-testid="workspace-apply"
                                          >
                                             {saving || activating
                                                ? 'Applying…'
                                                : store.status === 'active'
                                                  ? 'Apply update'
                                                  : 'Publish draft'}
                                          </button>
                                       </div>
                                       <p className="cw-inline-note">
                                          {store.status === 'active'
                                             ? 'Applies the reviewed draft to this running campaign.'
                                             : `Publishing keeps this campaign ${store.status}. It does not start outreach.`}
                                       </p>
                                    </div>
                                 )}
                                 {store.status !== 'active' && !store.dirty && (
                                    <div className="cw-surface">
                                       <h3>
                                          {store.status === 'paused'
                                             ? 'Resume campaign'
                                             : 'Activate campaign'}
                                       </h3>
                                       <p className="cw-inline-note">
                                          Activation applies the saved draft,
                                          purchases any reserved numbers, and
                                          allows eligible scheduled work to run.
                                       </p>
                                       <button
                                          type="button"
                                          className="cw-button primary"
                                          disabled={
                                             !serverPreview?.ready ||
                                             saving ||
                                             activating ||
                                             !!store.conflict
                                          }
                                          onClick={async () => {
                                             if (
                                                window.confirm(
                                                   'Activate this campaign and allow eligible outreach to run?'
                                                )
                                             )
                                                await activate()
                                          }}
                                          data-testid="workspace-activate"
                                       >
                                          {store.status === 'paused'
                                             ? 'Resume campaign'
                                             : 'Activate campaign'}
                                       </button>
                                    </div>
                                 )}
                                 {applyError && (
                                    <p
                                       role="alert"
                                       className="cw-banner cw-error"
                                    >
                                       {applyError}
                                    </p>
                                 )}
                              </div>
                           )}
                        </fieldset>
                     </main>
                  </div>
               </>
            )}
         </div>
      </FlowShell>
   )
}
